/**
 * Moving between pages without a reload, so state held in memory (the files a visitor just chose)
 * survives the move. `navigate` pushes a history entry and tells `usePath` subscribers. The Back
 * button arrives as popstate, which `usePath` hears as well.
 */
import { useSyncExternalStore } from "react";

const CHANGED = "lattice:navigate";

/**
 * A page holding typed input that has not been saved registers one of these. `ask` is handed what
 * to run when the person agrees to leave, and shows its own question.
 */
interface Guard {
  readonly dirty: () => boolean;
  readonly ask: (leave: () => void) => void;
}

const guards = new Set<Guard>();

function dirtyGuard(): Guard | null {
  return [...guards].find((candidate) => candidate.dirty()) ?? null;
}

/**
 * Holds in-app moves (`navigate`, Back and Forward) while `dirty()` is true, and asks first. A
 * reload or closing the tab gets the browser's own prompt. Returns the function that lifts it.
 */
export function guardNavigation(next: Guard): () => void {
  guards.add(next);

  const stop = (event: BeforeUnloadEvent) => {
    if (next.dirty()) event.preventDefault();
  };

  window.addEventListener("beforeunload", stop);

  return () => {
    window.removeEventListener("beforeunload", stop);
    guards.delete(next);
  };
}

/** Runs `proceed` now, or after the person agrees to lose what a guarded page holds. */
export function confirmLeave(proceed: () => void): void {
  const dirty = dirtyGuard();

  if (dirty === null) proceed();
  else dirty.ask(proceed);
}

/* Every entry carries its place in the session's history, so a popstate says how far it moved. */
function stamp(index: number) {
  return { index };
}

function indexOfEntry(): number {
  const index = window.history.state?.index;

  return Number.isInteger(index) ? index : 0;
}

/* The page the person is still looking at while a guarded move is being undone. */
let held: string | null = null;

function go(path: string): void {
  window.history.pushState(stamp(indexOfEntry() + 1), "", path);
  window.dispatchEvent(new Event(CHANGED));
  window.scrollTo(0, 0);
}

/** `force` skips the guard for a move that throws the input away anyway, such as signing out. */
export function navigate(path: string, options?: { readonly force?: boolean }): void {
  if (window.location.pathname + window.location.search === path) return;

  const dirty = options?.force === true ? null : dirtyGuard();

  if (dirty === null) go(path);
  else dirty.ask(() => go(path));
}

/** Rewrites the address of the current entry and keeps its place in the session's history. */
export function keepEntry(url: string): void {
  window.history.replaceState(stamp(indexOfEntry()), "", url);
}

/** Replace the current history entry. Used when an old comparison slug redirects. */
export function replace(path: string): void {
  if (window.location.pathname + window.location.search === path) return;
  window.history.replaceState(stamp(indexOfEntry()), "", path);
  window.dispatchEvent(new Event(CHANGED));
}

/*
  Back, Forward and a jump several entries long have moved the address by the time popstate fires.
  A guarded page undoes the move with `history.go` by the opposite distance, which keeps every entry
  in both directions, and asks. Agreeing repeats the move. `held` keeps the page on screen in
  between, so the form is not unmounted while the browser walks back. A second Back before the
  first is undone is undone the same way, until the address is where it was. This listener is added
  before any `usePath` subscriber's, so `held` is set before React reads the address.

  `history.go` does nothing when the entry it names is gone, and then no popstate follows. The
  timer gives up waiting, so the router never stays frozen on a stale page.
*/
if (typeof window !== "undefined") {
  const GIVE_UP_MS = 500;

  window.history.replaceState(stamp(indexOfEntry()), "");

  let here = indexOfEntry();
  let herePath = window.location.pathname;
  let restoring = false;
  let leaving = false;
  /* A Discard pressed while the guarded page is still being restored. It leaves once that is done. */
  let leaveBy: number | null = null;
  let giveUp: ReturnType<typeof setTimeout> | undefined;

  const settle = () => {
    clearTimeout(giveUp);
    restoring = false;
    leaving = false;
    leaveBy = null;
    held = null;
  };

  const leaveNow = (delta: number) => {
    leaving = true;
    held = null;
    jump(delta);
  };

  const jump = (by: number) => {
    clearTimeout(giveUp);
    giveUp = setTimeout(() => {
      settle();
      here = indexOfEntry();
      herePath = window.location.pathname;
      window.dispatchEvent(new Event(CHANGED));
    }, GIVE_UP_MS);
    window.history.go(by);
  };

  window.addEventListener("popstate", () => {
    /*
      A fragment link makes an entry with no state, which would read as the first entry of the
      session. It is a move forward within the page, so it takes the next index and the guard asks nothing.
    */
    if (!restoring && !Number.isInteger(window.history.state?.index)) {
      window.history.replaceState(stamp(here + 1), "");
      here += 1;
      herePath = window.location.pathname;

      return;
    }

    const arrived = indexOfEntry();

    if (restoring) {
      if (arrived !== here) jump(here - arrived);
      else {
        const wanted = leaveBy;

        settle();

        if (wanted !== null) leaveNow(wanted);
      }

      return;
    }

    const dirty = leaving ? null : dirtyGuard();
    const delta = arrived - here;

    if (leaving) settle();

    if (dirty === null || delta === 0) {
      here = arrived;
      herePath = window.location.pathname;

      return;
    }

    held = herePath;
    restoring = true;
    jump(-delta);
    dirty.ask(() => {
      if (restoring) leaveBy = delta;
      else leaveNow(delta);
    });
  });
  window.addEventListener(CHANGED, () => {
    here = indexOfEntry();
    herePath = window.location.pathname;
  });
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener("popstate", onChange);
  window.addEventListener(CHANGED, onChange);

  return () => {
    window.removeEventListener("popstate", onChange);
    window.removeEventListener(CHANGED, onChange);
  };
}

export function usePath(): string {
  return useSyncExternalStore(subscribe, () => held ?? window.location.pathname);
}
